import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import ResetIcon from 'src/assets/images/common/reset.svg';
import { useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import useApi from 'src/hooks/useApi';
import services from 'src/api/service';
import { useAmzContext } from 'src/contexts/AmzContext';
import { useFkContext } from 'src/contexts/FkContext';
import { useInstaMartContext } from 'src/contexts/InstamartContext';
import { useMyntraContext } from 'src/contexts/MyntraContext';
import { useBlinkitContext } from 'src/contexts/BlinkitContext';
import { useZeptoContext } from 'src/contexts/ZeptoContext';
import { useBigbasketContext } from 'src/contexts/BigbasketContext';

export default function AccountsReset({ onboardingProceed = false, redirectTo = null }) {
  const location = useLocation();
  const ParseData = JSON.parse(localStorage.getItem('User'));

  const { amzDispatch } = useAmzContext();
  const { fkDispatch } = useFkContext();
  const { instamartDispatch } = useInstaMartContext();
  const { myntraDispatch } = useMyntraContext();
  const { blinkitDispatch } = useBlinkitContext();
  const { zeptoDispatch } = useZeptoContext();
  const { bigbasketDispatch } = useBigbasketContext();

  const [isLoading, setIsLoading] = useState(false);
  
  const useLocalStoreParam = useMemo(() => {
    return window.location.href.indexOf('flipkart') > -1
      ? 'flipkartStore'
      : window.location.href.indexOf('myntra') > -1
      ? 'myntraStore'
      : window.location.href.indexOf('blinkit') > -1
      ? 'blinkitStore'
      : window.location.href.indexOf('instamart') > -1
      ? 'instamartStore'
      : window.location.href.indexOf('zepto') > -1
      ? 'zeptoStore'
       : window.location.href.indexOf('bigbasket') > -1
      ? 'bigbasketStore'
      : 'amazonStore';
  }, [window.location.href]);

  const useLocalStoreData = useMemo(() => {
    return JSON.parse(localStorage.getItem(useLocalStoreParam)) || [];
  }, [useLocalStoreParam]);

  const doGetProfiles = useApi(services.postService);

  const [profiles, setProfiles] = useState(useLocalStoreData)

  const doFetchApiData = useCallback(() => {
    setIsLoading(true)
    if (location.pathname.indexOf('amazon') > -1 || onboardingProceed) {
      doGetProfiles.request({ url: '/profiles/mdb-get-profiles', nodeAPI: 1, payload: { active: true } });
    } else if (location.pathname.indexOf('flipkart') > -1) {
      doGetProfiles.request({ url: '/flipkart/clientList', nodeAPI: 1 });
    } else if (location.pathname.indexOf('myntra') > -1) {
      doGetProfiles.request({ url: '/myntra/clientList', nodeAPI: 1 });
    } else if (location.pathname.indexOf('blinkit') > -1) {
      doGetProfiles.request({ url: '/blinkit/clientList', nodeAPI: 1 });
    } else if (location.pathname.indexOf('instamart') > -1) {
      doGetProfiles.request({ url: '/instamart/clientList', nodeAPI: 1 });
    } else if (location.pathname.indexOf('zepto') > -1) {
      doGetProfiles.request({ url: '/zepto/clientList', nodeAPI: 1 });
    } else if (location.pathname.indexOf('bigbasket') > -1) {
      doGetProfiles.request({ url: '/bigbasket/clientList', nodeAPI: 1 });
    } 
  }, [location.pathname, onboardingProceed]);

  const platformMap = useMemo(() => {
    return {
      flipkart: {
        apiCall: doGetProfiles,
        localProfileKey: 'flipkartProfile',
        localProfilesKey: 'flipkartProfiles',
        stateSetter: setProfiles,
        dispatcher: fkDispatch,
        actionType: 'UPDATE_FLIPKART_PROFILES'
      },
      instamart: {
        apiCall: doGetProfiles,
        localProfileKey: 'instamartProfile',
        localProfilesKey: 'instamartProfiles',
        stateSetter: setProfiles,
        dispatcher: instamartDispatch,
        actionType: 'UPDATE_INSTAMART_PROFILES'
      },
      myntra: {
        apiCall: doGetProfiles,
        localProfileKey: 'myntraProfile',
        localProfilesKey: 'myntraProfiles',
        stateSetter: setProfiles,
        dispatcher: myntraDispatch,
        actionType: 'UPDATE_MYNTRA_PROFILES'
      },
      blinkit: {
        apiCall: doGetProfiles,
        localProfileKey: 'blinkitProfile',
        localProfilesKey: 'blinkitProfiles',
        stateSetter: setProfiles,
        dispatcher: blinkitDispatch,
        actionType: 'UPDATE_BLINKIT_PROFILES'
      },
      amazon: {
        apiCall: doGetProfiles,
        localProfileKey: 'selectedProfileId',
        localProfilesKey: 'active-profiles-ids',
        stateSetter: setProfiles,
        dispatcher: amzDispatch,
        actionType: 'UPDATE_AMAZON_ACTIVE_PROFILES',
        defaultPayload: {
          active: true
        }
      },
      zepto: {
        apiCall: doGetProfiles,
        localProfileKey: 'zeptoProfile',
        localProfilesKey: 'zeptoProfiles',
        stateSetter: setProfiles,
        dispatcher: zeptoDispatch,
        actionType: 'UPDATE_ZEPTO_PROFILES'
      },
      bigbasket: {
        apiCall: doGetProfiles,
        localProfileKey: 'bigbasketProfile',
        localProfilesKey: 'bigbasketProfiles',
        stateSetter: setProfiles,
        dispatcher: bigbasketDispatch,
        actionType: 'UPDATE_BIGBASKET_PROFILES'
      },
    };
  }, [
    fkDispatch,
    instamartDispatch,
    myntraDispatch,
    blinkitDispatch,
    amzDispatch,
    doGetProfiles,
    window.location.href,
    useLocalStoreData,
    useLocalStoreParam,
    setProfiles,
    zeptoDispatch,
    bigbasketDispatch,
  ]);

  const platformKey = useMemo(() => {
    return useLocalStoreParam?.replace('Store', '').toLowerCase();
  }, [useLocalStoreParam]);

  const platformData = useMemo(() => {
    return platformMap?.[platformKey];
  }, [platformMap, platformKey]);

  useEffect(() => {
    const { loading, data } = doGetProfiles;
    if(!loading && data) {
      doProceeedLoad()
    }
  }, [
    doGetProfiles.loading
  ])


  const doProceeedLoad = useCallback(() => {
    console.log("doProceeedLoadData", platformData)
    if (platformData) {
      console.log("doProceeedloading", platformData?.apiCall)
      const { apiCall, localProfileKey, localProfilesKey, stateSetter, dispatcher, actionType } = platformData;
      const { loading, data } = apiCall;
      if (!loading && data && (data?.status === true || data?.success === true) && Array.isArray(data.data)) {
        const profileData = data.data.map((rowItem) => ({
          countryCode: rowItem?.countryCode || 'IN',
          currencyCode: rowItem?.currencyCode || 'INR',
          timezone: rowItem?.timezone || 'Asia/Kolkata',
          profileId: rowItem.profileId,
          accessLevel: rowItem.accessLevel || 'Admin',
          profileOwner: rowItem.profileOwner || ParseData?.email,
          ...rowItem,
          accountInfo: {
            type: 'advertising',
            ...rowItem?.accountInfo,
            name: rowItem?.accountInfo?.name || rowItem.Lable,
          },
        }));
        const dspAccountData = data?.dspAccounts?.map((dspItem)=>({
          ...dspItem,
          amazonManaged:dspItem?.amazonManaged,
          countryCode: dspItem?.countryCode || 'IN',
          currencyCode: dspItem?.currencyCode || 'INR',
          timezone: dspItem?.timezone || 'Asia/Kolkata',
          dspClientName: dspItem.dspClientName,
          accountInfo:{
            id: dspItem.dspId,
            name: dspItem.dspClientName,
            type:'DSP'
          },
          datasetId: dspItem.dspdatasetId,
          displayTypeTitle: 'DSP',
          profileId: dspItem?.agencyProfileId,
          profileOwner:dspItem.clientEmail,
          accessLevel: dspItem?.accessLevel || 'Admin',
          dsp_only: true,
          marketplace: `${CurrencyCodeMarketPlace[dspItem?.currencyCode || 'INR'] || 'europe'}`,
          dsp: [dspItem]
        })) || []
        const pendingDspAccountData = data?.dspPendingAccounts?.map((dspItem)=>({
          ...dspItem,
          countryCode: dspItem?.countryCode || 'IN',
          currencyCode: dspItem?.currencyCode || 'INR',
          timezone: dspItem?.timezone || 'Asia/Kolkata',
          dspClientName: dspItem?.dspClientName,
          displayTypeTitle: 'DSP',
          profileOwner:dspItem.clientEmail,
          accessLevel: dspItem?.accessLevel || 'Admin',
          dsp_only: true,
          dsp: [dspItem],
          accountInfo:{
              name: dspItem.dspClientName,
              type:'DSP'
          },
          isDspPendingRequest: true,
        })) || []
        const mdbData = [...profileData, ...dspAccountData, ...pendingDspAccountData]
        doUpdateProfile({profileData: mdbData, localProfileKey, localProfilesKey, stateSetter, dispatcher, actionType});
      } else {
        doUpdateProfile({profileData: [], localProfileKey, localProfilesKey, stateSetter, dispatcher, actionType});
      }
    }
  }, [JSON.stringify(platformData), useLocalStoreData, platformData?.apiCall?.loading, useLocalStoreData ]);

  useEffect(() => {
    if(isLoading) {
      document?.body?.classList?.add('make-disabled');
    }
  }, [isLoading])

  const doUpdateProfile = useCallback(({profileData, localProfileKey, localProfilesKey, stateSetter, dispatcher, actionType}) => {
    console.log("doUpdateProfile", profileData, localProfileKey, localProfilesKey, stateSetter, dispatcher, actionType)
    if (profileData && profileData.length > 0) {
      setTimeout(() => {
        const currentProfile = JSON.parse(sessionStorage.getItem(localProfileKey));
        console.log("<<currentProfile>>", currentProfile)

        const onboardedAgencyProfiles = JSON.parse(localStorage.getItem('OnBoardedAgencyProfiles'));
        // If onboarded agency profiles are present, then preselect the profile
        if (onboardingProceed && onboardedAgencyProfiles && onboardedAgencyProfiles?.length > 0) {
          const preselectedProfile = profileData.find(
            (profile) => onboardedAgencyProfiles.includes(profile?.profileId)
          );
          // If preselected profile is found, then set it as the current profile
          // If not found, then continue with the normal flow
          if (preselectedProfile && preselectedProfile?.profileId) {
            sessionStorage.setItem(localProfileKey, JSON.stringify(preselectedProfile));
            localStorage.removeItem('OnBoardedAgencyProfiles');
            
            stateSetter(profileData);
            localStorage.setItem(useLocalStoreParam, JSON.stringify(profileData));
            dispatcher({ type: actionType, payload: profileData });
            redirectTo && window.location.assign(redirectTo);
            return;
          }
        } else if (currentProfile?.profileId) {
          const selectedProfile = profileData.find(
            (profile) =>
            ((!currentProfile?.dsp_only && profile?.profileId == currentProfile?.profileId &&
              profile?.accessLevel === currentProfile?.accessLevel &&
              profile?.profileOwner === currentProfile?.profileOwner) || (currentProfile?.dsp_only === true && profile?.dspId === currentProfile?.dspId))
          );
          console.log("<<selectedProfile>>", selectedProfile)
          const profileDetails = selectedProfile || profileData[0]
          sessionStorage.setItem(localProfileKey, JSON.stringify(profileDetails));
          if(localProfileKey === 'selectedProfileId' && profileDetails && profileDetails?.ams?.groupedAccounts?.length > 0) {
            const groupedProfiles = [  ...[profileDetails] ,...profileDetails?.ams?.groupedAccounts]
            const selectedGroupProfileId = JSON.parse(sessionStorage.getItem('selectedGroupedProfileId'))
            if(selectedGroupProfileId && groupedProfiles.some((item) => item?.profileId === selectedGroupProfileId?.profileId)) {
              sessionStorage.setItem('selectedGroupedProfileId', JSON.stringify(groupedProfiles.find((item) => item?.profileId == selectedGroupProfileId?.profileId)))
            } else {
              sessionStorage.setItem('selectedGroupedProfileId', JSON.stringify(profileDetails))
            }
          } else {
            sessionStorage.setItem('selectedGroupedProfileId', JSON.stringify({}))
          }
        } else {
          const profileDetails = profileData?.[0]
          sessionStorage.setItem(localProfileKey, JSON.stringify(profileDetails));
          if(localProfileKey === 'selectedProfileId' && profileDetails && profileDetails?.ams?.groupedAccounts?.length > 0) {
            const groupedProfiles = [  ...[profileDetails] , ...profileDetails?.ams?.groupedAccounts]
            const selectedGroupProfileId = JSON.parse(sessionStorage.getItem('selectedGroupedProfileId'))
            if(selectedGroupProfileId && groupedProfiles.some((item) => item?.profileId === selectedGroupProfileId?.profileId)) {
              sessionStorage.setItem('selectedGroupedProfileId', JSON.stringify(groupedProfiles.find((item) => item?.profileId == selectedGroupProfileId?.profileId)))
            } else {
              sessionStorage.setItem('selectedGroupedProfileId', JSON.stringify(profileDetails))
            }
          } else {
            sessionStorage.setItem('selectedGroupedProfileId', JSON.stringify({}))
          }
        }
        stateSetter(profileData);
        localStorage.setItem(useLocalStoreParam, JSON.stringify(profileData));
        dispatcher({ type: actionType, payload: profileData });
        setIsLoading(false)
        window.location.assign(location.pathname)
      }, 1500);
    } else {
      stateSetter([]);
      sessionStorage.setItem(localProfileKey, JSON.stringify({}));
      dispatcher({ type: actionType, payload: [] });
      setIsLoading(false)
      localStorage.setItem(useLocalStoreParam, JSON.stringify([]));
      window.location.assign(location.pathname)
    }
  }, [useLocalStoreParam, location.pathname])

  useEffect(() => {
    if(onboardingProceed) {
      doFetchApiData();
    }
  }, [onboardingProceed])

  return (
    <>
      <button
        className={`cursor-pointer list-accounts-button d-inline-block m-b-0 f-w-700 f-14 text-dark ${onboardingProceed ? 'display-none' : ''} ${isLoading || onboardingProceed ? 'disabled' : ''}`}
        style={{ float: 'right' }}
        disabled={isLoading}
        onClick={() => {
          if(!isLoading) doFetchApiData();
        }}
      >
        ACCOUNTS
        {isLoading ? (
          <CircularProgress
            size={16}
            color="inherit"
            sx={{ ml: 1, verticalAlign: 'middle' }}
            aria-label="Loading"
          />
        ) : (
          <img src={ResetIcon} alt="reset" className="wid-16 ml-2" />
        )}
      </button>
    </>
  );
}


const CurrencyCodeMarketPlace = {
  "USD": "north_america",
  "AUD": "east",
  "SAR": "europe",
  "SEK": "europe",
  "BRL": "north_america",
  "GBP": "europe",
  "EUR": "europe",
  "PLN": "europe",
  "INR": "europe",
  "MXN": "north_america",
  "CAD": "north_america",
  "AED": "europe"
}